import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";

axios.defaults.withCredentials = true;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

const getDeviceId = () => {
  let id = localStorage.getItem("vt_device_id");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("vt_device_id", id);
  }
  return id;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("vt_ref", ref);
    if (!localStorage.getItem("vt_origin")) {
      const utm = params.get("utm_source") || params.get("utm_campaign");
      const origin = utm ? `utm:${utm}` : (document.referrer || "directo");
      localStorage.setItem("vt_origin", origin.slice(0, 300));
    }
    refreshUser();
  }, [refreshUser]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Flujo directo: popup oficial del selector de cuentas de Google (sin pantallas intermedias)
  const googleLogin = useGoogleLogin({
    flow: "implicit",
    scope: "openid email profile",
    onSuccess: async (tokenResponse) => {
      try {
        const r = await axios.post(`${API}/auth/google`, {
          access_token: tokenResponse.access_token,
          ref: localStorage.getItem("vt_ref") || null,
          device_id: getDeviceId(),
        });
        setUser(r.data);
        localStorage.removeItem("vt_ref");
      } catch {
        setUser(null);
      } finally {
        setLoggingIn(false);
      }
    },
    onError: () => setLoggingIn(false),
    onNonOAuthError: () => setLoggingIn(false),
  });

  const login = () => {
    setLoggingIn(true);
    googleLogin();
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // ignore
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, authLoading, loggingIn, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
